import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { propEq } from 'ramda'
import { Link } from 'react-router-dom'

import strings from 'utils/strings'
import { sumProp } from 'utils/functional'
import { moduleFeatureResourceCounts } from 'utils/truck'
import { insertIf } from 'utils/types'
import { displayModuleName } from 'i18n/displayConstants'
import { hasRight } from 'selectors/authorization'
import { VIEW_FEATURE_REVISION } from 'constants/authorization'
import { useSelector } from 'hooks/redux'

import { Button, Flex, Tooltip } from 'components/patterns'
import { Icon } from 'components/patterns/Icon/Icon'
import { DYNAMIC } from 'constants/featureTypes'
import { OBSOLETE } from 'utils/constants'
import MoreInfo from './MoreInfo'
import CollapsibleDesc from './MoreInfo/CollapsibleDesc'
import { StyledTable, SupLimitExceeded } from './FeaturesTable.style'
import {
  hasNewerEligibleRevision,
  hasRenderableWarning,
} from './updateEligibility'
import { getMaxNotificationString } from '../AddRemoveVehicleFeatures/utils'

const FeaturesTable = ({
  isEditable,
  features,
  renderCustomCol,
  allowNav,
  customColWidth,
  isSummary,
  isQPM,
  showSwitchIndicators,
  onDynamicFeatureClick,
  hideAlertInfo,
  vehicleECUs,
  vehicleSubType,
  vehicle35mCode,
  buildDate,
}) => {
  const canViewRevision = useSelector(hasRight, VIEW_FEATURE_REVISION)
  const [collapsibleState, setCollapsibleState] = useState([])

  const handleExpandCollapse = (record) => {
    const matchIndex = collapsibleState.findIndex((item) => item === record.id)

    if (matchIndex !== -1) {
      setCollapsibleState((prev) =>
        prev.slice(0, matchIndex).concat(prev.slice(matchIndex + 1))
      )
    } else {
      setCollapsibleState((prev) => [...prev, record.id])
    }
  }

  // TODO: fix vehicle/features selector, it returns a new object for
  // features every render

  // Bug #505985: the alert column is inserted exactly when MoreInfo will render
  // something in it. Both the gate and the cell go through hasRenderableWarning,
  // which also accounts for moreInfoAlertCol's own render guard, so the column can
  // neither appear empty nor be missing for a row that has a warning to show.
  //
  // moduleCounts is computed inside getColumns, so it is threaded in per call.
  const hasAlertWorthyFeatures = (isEditableMode, moduleCounts) =>
    features.some((feat) =>
      hasRenderableWarning(feat, {
        isEditable: isEditableMode,
        hideAlertInfo,
        moduleCounts,
        vehicleECUs,
      })
    )

  const getColumns = () => {
    const logicSetTotal = sumProp('complexQueryCount', features)
    const timersTotal = sumProp('timerQueryCount', features)
    const switchIndicatorsTotal = new Set(
      features.flatMap((item) => item.switchIndicators).filter((item) => item)
    ).size

    const canInputsTotal = new Set(
      features.flatMap((item) => item.canInputs).filter((item) => item)
    ).size

    const moduleCounts = moduleFeatureResourceCounts(features)

    const moreInfoAlertCol = {
      dataIndex: 'latestOnVehicle',
      render: (_, record) => {
        // Never suppress the cell when the record has a VPDM flag — the VPDM
        // warning lives in MoreInfo and must not be blocked by the dynamic-
        // feature or marked-for-addition short-circuits below.
        const hasVpdm = record.isFeatureVpdmCompatible
        const suppressForDynamic = propEq('type', DYNAMIC, record)
        const suppressForMarkedAddition =
          hideAlertInfo && record.isMarkedForAddition

        if (!hasVpdm && (suppressForDynamic || suppressForMarkedAddition)) {
          return null
        }

        return (
          <MoreInfo
            record={record}
            moduleCounts={moduleCounts}
            isEditable={isEditable}
            isSummary={isSummary}
            features={features}
            vehicleECUs={vehicleECUs}
            vehicleSubType={vehicleSubType}
            vehicle35mCode={vehicle35mCode}
            buildDate={buildDate}
          />
        )
      },
      width: 50,
      key: 'latestOnVehicle',
      align: 'center',
    }

    const editableCol = {
      dataIndex: 'id',
      render: renderCustomCol,
      width: customColWidth || 50,
      key: `customColumn`,
      align: 'center',
      className: 'custom-column',
    }

    const cellStyle = {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }

    const defaultCols = [
      {
        title: strings.feature_no,
        dataIndex: 'partNumber',
        onCell: () => ({ style: cellStyle }),
        render: (partNumber, record) => {
          if (propEq('type', DYNAMIC, record)) {
            let icon = 'caret_drop_down'
            if (collapsibleState.includes(record.id)) {
              icon = 'caret_drop_up'
            }
            return (
              <Flex
                alignItems="center"
                justifyContent="space-between"
                style={{ cursor: 'pointer' }}
              >
                <Tooltip
                  placement="right"
                  title={strings.dynamic_feature_tooltip_text}
                  overlayClassName="dynamicFeatureTooltip"
                  overlayStyle={{
                    width: 338,
                    minWidth: 338,
                    '& ant-tooltip-inner': {
                      whiteSpace: 'normal',
                    },
                  }}
                >
                  <Button
                    link
                    onClick={(e) => {
                      e.preventDefault()
                      onDynamicFeatureClick(record)
                    }}
                    style={{
                      whiteSpace: 'pre',
                    }}
                  >
                    {partNumber}
                  </Button>
                </Tooltip>
                <Icon
                  icon={icon}
                  width={25}
                  height={25}
                  onClick={() => handleExpandCollapse(record)}
                />
              </Flex>
            )
          }
          if (record.uri.length > 0 && allowNav) {
            return <Link to={`/features/${record.id}`}>{partNumber}</Link>
          }
          return <span>{partNumber}</span>
        },
        width: 140,
      },
      ...(canViewRevision
        ? [
            {
              title: strings.revision_short_dot,
              dataIndex: 'revision',
              width: 50,
              onCell: () => ({ style: cellStyle }),
              render: (revision) => revision || '-',
            },
          ]
        : []),
      {
        title: strings.description,
        dataIndex: 'description',
        onCell: () => ({ style: cellStyle }),
        sorter: (a, b) => a.description.localeCompare(b.description),
        render: (_, record) => (
          <CollapsibleDesc
            record={record}
            data={features}
            isExpanded={collapsibleState.includes(record.id)}
          />
        ),
        className: 'description-column',
      },
      {
        title: strings.ecu,
        dataIndex: 'moduleName',
        width: 90,
        onCell: () => ({ style: cellStyle }),
        render: displayModuleName,
        sorter: (a, b) => a.moduleName.localeCompare(b.moduleName),
      },
      {
        title: strings.master_no,
        dataIndex: 'masterNumber',
        width: 90,
        onCell: () => ({ style: cellStyle }),
      },
      {
        title: strings.release_no,
        dataIndex: 'releaseNumber',
        width: 90,
        onCell: () => ({ style: cellStyle }),
      },
      {
        title: strings.status,
        dataIndex: 'status',
        width: 90,
        onCell: () => ({ style: cellStyle }),
      },
      {
        title: (
          <span>
            {strings.logic_sets_used} <sup>{logicSetTotal}</sup>
          </span>
        ),
        dataIndex: 'complexQueryCount',
        width: 90,
        onCell: () => ({ style: cellStyle }),
      },
      {
        title: (
          <span>
            {strings.timers_used} <sup>{timersTotal}</sup>
          </span>
        ),
        dataIndex: 'timerQueryCount',
        width: 90,
        onCell: () => ({ style: cellStyle }),
      },
    ]

    if (isQPM) {
      defaultCols.push({
        title: (
          <span>
            {strings.can_inputs_used}{' '}
            <SupLimitExceeded
              isLimitExceeded={Boolean(
                getMaxNotificationString(
                  'can_inputs',
                  features.filter((item) => !item.isMarkedForDeletion)
                )
              )}
            >
              {canInputsTotal}
            </SupLimitExceeded>
          </span>
        ),
        dataIndex: 'canInputs',
        onCell: () => ({ style: cellStyle }),
        render: (canInputs) => (
          <span>{!canInputs ? strings.n_a : canInputs.length}</span>
        ),
        width: 100,
      })
    }

    if (isQPM && showSwitchIndicators) {
      defaultCols.push({
        title: (
          <span>
            {strings.switch_indicators_used} <sup>{switchIndicatorsTotal}</sup>
          </span>
        ),
        dataIndex: 'switchIndicators',
        onCell: () => ({ style: cellStyle }),
        render: (switchIndicators) => (
          <span>
            {!switchIndicators ? strings.n_a : switchIndicators.length}
          </span>
        ),
        width: 125,
      })
    }

    if (isEditable) {
      // The dedicated resource-limit scan that used to also gate this column is
      // gone. It only mattered while the orange per-row icon carried normal
      // updates and the triangle was reserved for the limit-exceeding ones; every
      // record it accepted is an eligible update, so hasRenderableWarning already
      // covers it — keeping it could only reintroduce empty cells for updates the
      // user is not authorized to apply.
      return [
        editableCol,
        ...insertIf(
          hasAlertWorthyFeatures(true, moduleCounts),
          moreInfoAlertCol
        ),
        ...defaultCols,
      ]
    }

    const showMoreInfoCol = hasAlertWorthyFeatures(false, moduleCounts)

    return [...insertIf(showMoreInfoCol, moreInfoAlertCol), ...defaultCols]
  }

  const sortedFeatures = useMemo(() => {
    // Bug #505985: was a fourth character-for-character copy of the revision
    // eligibility rules. Uses the shared helper now. The sort intentionally keeps
    // the coarser `latestOnVehicle === false` below rather than requiring an
    // eligible snapshot, so row order is unchanged by this PR.
    const hasNewerRevision = hasNewerEligibleRevision

    const hasWarning = (f) =>
      f.status === OBSOLETE ||
      f.latestOnVehicle === false ||
      f.isCompatible === false ||
      hasNewerRevision(f)

    const sorted = [...features].sort((a, b) => {
      const aWarn = hasWarning(a)
      const bWarn = hasWarning(b)
      if (aWarn && !bWarn) return -1
      if (bWarn && !aWarn) return 1

      // Within warnings, obsolete first
      if (a.status === OBSOLETE && b.status !== OBSOLETE) return -1
      if (b.status === OBSOLETE && a.status !== OBSOLETE) return 1

      return 0
    })
    return sorted
  }, [features])

  return (
    <StyledTable
      className="features-table-fixed-layout"
      size="middle"
      columns={getColumns()}
      dataSource={sortedFeatures}
      pagination={false}
      rowKey={(record) =>
        `${record.partNumber}-${record.description}-${record.module}`
      }
      rowClassName={(record) =>
        propEq('type', DYNAMIC, record) ? 'dynamic-feature-row' : ''
      }
    />
  )
}

FeaturesTable.propTypes = {
  features: PropTypes.array.isRequired,
  isEditable: PropTypes.bool,
  renderCustomCol: PropTypes.func,
  allowNav: PropTypes.bool,
  customColWidth: PropTypes.number,
  isSummary: PropTypes.bool,
  isQPM: PropTypes.bool,
  showSwitchIndicators: PropTypes.bool,
  highlightOverLimts: PropTypes.bool,
  hideAlertInfo: PropTypes.bool,
  onDynamicFeatureClick: PropTypes.func.isRequired,
  vehicleECUs: PropTypes.array,
  vehicleSubType: PropTypes.string,
  vehicle35mCode: PropTypes.string,
  buildDate: PropTypes.string,
}

FeaturesTable.defaultProps = {
  isEditable: false,
  renderCustomCol: null,
}

export default FeaturesTable
